// Shared display helpers for the TruCheck cost UI (admin cost screen + the TruCheck detail
// cost section). Keeps item-type / model labels and money formatting consistent everywhere.

// Human labels for every cost item type the backend records.
export const COST_ITEM_LABELS = {
    claude_input_tokens: "Claude — Input tokens",
    claude_output_tokens: "Claude — Output tokens",
    claude_cache_read_tokens: "Claude — Cache read",
    claude_cache_write_tokens: "Claude — Cache write",
    veriff_session: "Veriff session",
    veriff_ssn_session: "Veriff SSN session",
    idi_ssn_session: "IDI SSN check",
    ssa89_ssn_session: "SSA89 SSN check",
    rekognition_compare_faces: "Rekognition (face compare)",
    qa_review: "QA team review",
}

export const costItemLabel = (itemType) =>
    COST_ITEM_LABELS[itemType] || itemType || "—"

// Friendly names for the Anthropic model ids; unknown ids fall back to the raw value.
const MODEL_LABELS = {
    "claude-sonnet-4-6": "Sonnet",
    "claude-haiku-4-5-20251001": "Haiku",
}

export const modelLabel = (model) => {
    if (!model) return null
    return MODEL_LABELS[model] || model
}

export const unitLabel = (unit) => {
    if (unit === "per_million_tokens") return "per 1M tokens"
    if (unit === "per_unit") return "per unit"
    return unit || ""
}

// USD amounts here span from dollars (QA fee) down to fractions of a cent (per-token), so
// the precision scales with the magnitude instead of clipping tiny costs to $0.00.
export const formatUsd = (amount, currency = "USD") => {
    const n = Number(amount) || 0
    const abs = Math.abs(n)
    let decimals = 2
    if (abs > 0 && abs < 0.01) decimals = 6
    else if (abs < 1) decimals = 4
    const symbol = currency === "USD" ? "$" : ""
    return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

// Fixed-decimal money for aligned cost columns: every value uses the SAME number of decimals
// so, when right-aligned with tabular-nums, the decimal points and every digit line up row to
// row. Use this (not formatUsd) wherever amounts are stacked in a column. 4 dp keeps sub-cent
// token costs visible while the dollar items (QA $1.5000, Veriff $0.8000) still align.
export const formatUsdAligned = (amount, currency = "USD", decimals = 4) => {
    const n = Number(amount) || 0
    const symbol = currency === "USD" ? "$" : ""
    return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

// Token / unit quantities — plain integer-ish grouping.
export const formatQuantity = (quantity) => {
    const n = Number(quantity) || 0
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

const round6 = (n) => Math.round(((n || 0) + Number.EPSILON) * 1e6) / 1e6

export const isClaudeItemType = (itemType) =>
    typeof itemType === "string" && itemType.startsWith("claude_")

// The four Claude token cost items, as ordered columns (input → cache write → cache read →
// output). Each is a separately-priced cost item; cache read is cheap, cache write dear.
export const CLAUDE_TOKEN_COLUMNS = [
    { itemType: "claude_input_tokens", key: "input", label: "Input" },
    { itemType: "claude_cache_write_tokens", key: "cacheWrite", label: "Cache write" },
    { itemType: "claude_cache_read_tokens", key: "cacheRead", label: "Cache read" },
    { itemType: "claude_output_tokens", key: "output", label: "Output" },
]

const TOKEN_KEY_BY_ITEM_TYPE = CLAUDE_TOKEN_COLUMNS.reduce((acc, c) => {
    acc[c.itemType] = c.key
    return acc
}, {})

// Pivot cost items (each { itemType, amount, sources:[{ label, amount, quantity }] }) into a
// Claude "source (analysis) × token type" matrix: one row per source with input / cacheWrite /
// cacheRead / output amounts + total, plus column totals. Works for both the per-TruCheck
// detail items and the admin source-breakdown items. Non-Claude items are ignored.
export const buildClaudeMatrix = (items = []) => {
    const rowsByLabel = new Map()
    const totals = { input: 0, cacheWrite: 0, cacheRead: 0, output: 0, total: 0 }

    for (const item of items) {
        const key = TOKEN_KEY_BY_ITEM_TYPE[item.itemType]
        if (!key) continue // non-Claude (or unknown) — skip
        for (const src of item.sources || []) {
            const label = src.label || "Other"
            const row = rowsByLabel.get(label) || { label, input: 0, cacheWrite: 0, cacheRead: 0, output: 0, total: 0 }
            row[key] = round6(row[key] + (src.amount || 0))
            row.total = round6(row.total + (src.amount || 0))
            rowsByLabel.set(label, row)
            totals[key] = round6(totals[key] + (src.amount || 0))
            totals.total = round6(totals.total + (src.amount || 0))
        }
    }

    const rows = [...rowsByLabel.values()].sort((a, b) => b.total - a.total)
    return { rows, totals, hasRows: rows.length > 0 }
}
